/**
 * Brown - collection scroll restore
 *
 * When a customer opens a product from a collection page and comes back,
 * return them to where they were instead of the top of the page.
 *
 * We remember WHICH product card was clicked, not just a pixel offset, and
 * scroll that card back into view. Anchoring to the card survives lazy-loaded
 * images and a changed viewport. The pixel offset is kept as a fallback.
 *
 * Note on the 'did they come back' test: the obvious check is a back_forward
 * navigation type or a product-page referrer. Neither fires on this store,
 * because Shopify serves collection pages uncached - which is exactly why the
 * browser loses the position. So a recent saved entry is the signal, with a
 * short window, and the back/referrer signals only widen that window.
 *
 * Safe by design: collections only; the entry is written on a product click
 * from that exact view, used once, then discarded; gives up after 2s; aborts
 * the moment the customer scrolls or taps; all storage access is wrapped.
 */
(function () {
  'use strict';

  var KEY = 'brown:collection-scroll';
  var WINDOW_SOFT_MS = 5 * 60 * 1000;
  var WINDOW_STRONG_MS = 30 * 60 * 1000;
  var SETTLE_MS = 2000;
  var CARD_SELECTOR = 'product-card, .product-item, .product-list__item, li';
  var ABORT_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

  function read(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function write(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function drop(k) { try { sessionStorage.removeItem(k); } catch (e) {} }

  function onCollectionPage() {
    return location.pathname.indexOf('/collections/') === 0 &&
           location.pathname.indexOf('/products/') === -1;
  }

  function viewKey() { return location.pathname + location.search; }

  function handleFromHref(href) {
    var m = String(href).match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  if (!onCollectionPage()) return;

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var link = target.closest('a[href*="/products/"]');
    if (!link) return;
    write(KEY, JSON.stringify({
      view: viewKey(),
      y: window.scrollY || window.pageYOffset || 0,
      handle: handleFromHref(link.getAttribute('href') || ''),
      at: Date.now()
    }));
  }, true);

  var raw = read(KEY);
  if (!raw) return;

  var saved;
  try { saved = JSON.parse(raw); } catch (e) { drop(KEY); return; }
  if (!saved || saved.view !== viewKey()) return;

  var nav = (window.performance && performance.getEntriesByType)
    ? performance.getEntriesByType('navigation')[0]
    : null;
  var strongSignal = (nav && nav.type === 'back_forward') ||
                     document.referrer.indexOf('/products/') > -1;

  var age = Date.now() - saved.at;
  if (age > (strongSignal ? WINDOW_STRONG_MS : WINDOW_SOFT_MS)) { drop(KEY); return; }

  drop(KEY);

  function targetOffset() {
    if (saved.handle) {
      var link = document.querySelector('a[href*="/products/' + saved.handle + '"]');
      if (link) {
        var card = link.closest(CARD_SELECTOR) || link;
        var rect = card.getBoundingClientRect();
        var current = window.scrollY || window.pageYOffset || 0;
        return Math.max(0, rect.top + current - (window.innerHeight - rect.height) / 2);
      }
    }
    var reachable = document.documentElement.scrollHeight - window.innerHeight;
    return reachable >= saved.y ? saved.y : null;
  }

  var previousRestoration = history.scrollRestoration;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  var deadline = Date.now() + SETTLE_MS;
  var timer = null;
  var stopped = false;

  function stop() {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = previousRestoration || 'auto';
    }
    ABORT_EVENTS.forEach(function (evt) {
      window.removeEventListener(evt, stop, true);
    });
  }

  ABORT_EVENTS.forEach(function (evt) {
    window.addEventListener(evt, stop, { capture: true, passive: true });
  });

  function tick() {
    if (stopped) return;
    var y = targetOffset();
    if (y !== null) window.scrollTo(0, y);
    if (Date.now() < deadline) {
      timer = setTimeout(tick, 50);
    } else {
      stop();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tick, { once: true });
  } else {
    tick();
  }
})();
